import React from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import { calculateProjectBudgetSummary } from '../../utils/budgetCalculations';
import { TactilePanel } from '../common/TactilePanel';
import { RESTORATION_PHASES, RestorationPhase } from '../../types/teardown';
import {
  DollarSign,
  TrendingDown,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Layers,
  Percent,
} from 'lucide-react';

export interface BudgetVarianceCardProps {
  onSelectPhase?: (phase: RestorationPhase | 'ALL') => void;
  selectedPhase?: RestorationPhase | 'ALL';
  className?: string;
}

export const BudgetVarianceCard: React.FC<BudgetVarianceCardProps> = ({
  onSelectPhase,
  selectedPhase = 'ALL',
  className = '',
}) => {
  const { state } = useRestoMod();
  const { project, bomItems } = state;

  const budgetCap = project?.budgetCap || 65000;
  const summary = calculateProjectBudgetSummary(budgetCap, bomItems);

  const formatCurrency = (val: number, includeCents = false) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: includeCents ? 2 : 0,
      maximumFractionDigits: includeCents ? 2 : 0,
    }).format(val);
  };

  const isProjectedOverBudget = summary.projectedBudgetVariance > 0;
  const isActualOverCap = summary.remainingBudget < 0;

  // Gauge bar coloring based on actual utilization
  const getGaugeColor = (pct: number) => {
    if (pct > 100) return 'bg-safety-red';
    if (pct >= 85) return 'bg-safety-amber';
    return 'bg-safety-green';
  };

  const phaseKeys = Object.keys(RESTORATION_PHASES) as RestorationPhase[];

  return (
    <TactilePanel
      title="Budget Variance & Sourcing Capital Ledger"
      subtitle={`Tracking ${bomItems.length} BOM components against allocated build budget cap`}
      badge={
        <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
          <DollarSign className="w-4 h-4" />
        </span>
      }
      action={
        <div className="flex items-center gap-2">
          {summary.projectedBudgetVariance <= 0 ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-bold uppercase rounded border bg-[#064e3b] text-[#ecfdf5] border-[#10b981]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Projected On Budget ({formatCurrency(Math.abs(summary.projectedBudgetVariance))})
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-bold uppercase rounded border bg-[#7f1d1d] text-[#fef2f2] border-[#dc2626]">
              <AlertTriangle className="w-3.5 h-3.5" />
              Projected Over Budget (+{formatCurrency(summary.projectedBudgetVariance)})
            </span>
          )}
        </div>
      }
      className={className}
    >
      <div className="space-y-6">
        {/* Top 4 Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* 1. Allocated Budget Cap */}
          <div className="bg-shop-darkest border border-shop-border rounded p-3.5 space-y-1">
            <div className="flex items-center justify-between text-shop-muted text-[11px] font-mono uppercase">
              <span>Allocated Cap</span>
              <Layers className="w-3.5 h-3.5 text-shop-steel" />
            </div>
            <div className="text-xl font-bold font-mono text-shop-highlight">
              {formatCurrency(summary.totalAllocatedBudget)}
            </div>
            <div className="text-[11px] text-shop-muted truncate">
              Vehicle Target: {project?.title || 'RestoMod Project'}
            </div>
          </div>

          {/* 2. Actual Spent (Sourced + Installed) */}
          <div className="bg-shop-darkest border border-shop-border rounded p-3.5 space-y-1">
            <div className="flex items-center justify-between text-shop-muted text-[11px] font-mono uppercase">
              <span>Actual Spent</span>
              <DollarSign className="w-3.5 h-3.5 text-safety-amber" />
            </div>
            <div className="text-xl font-bold font-mono text-safety-amber">
              {formatCurrency(summary.totalActualSpent, true)}
            </div>
            <div className="text-[11px] text-shop-muted flex items-center justify-between">
              <span>Utilization:</span>
              <span className="font-mono font-bold text-shop-light">
                {summary.budgetUtilizationPct.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* 3. Pending Committed (Needed Parts Estimated) */}
          <div className="bg-shop-darkest border border-shop-border rounded p-3.5 space-y-1">
            <div className="flex items-center justify-between text-shop-muted text-[11px] font-mono uppercase">
              <span>Pending Sourcing</span>
              <Clock className="w-3.5 h-3.5 text-blueprint-cyan" />
            </div>
            <div className="text-xl font-bold font-mono text-blueprint-cyan">
              {formatCurrency(summary.totalPendingCommitted, true)}
            </div>
            <div className="text-[11px] text-shop-muted">
              Estimated unfulfilled items
            </div>
          </div>

          {/* 4. Projected Total & Net Variance */}
          <div
            className={`border rounded p-3.5 space-y-1 ${
              isProjectedOverBudget
                ? 'bg-[#1e1112] border-safety-red text-safety-red'
                : 'bg-shop-darkest border-safety-green text-safety-green'
            }`}
          >
            <div className="flex items-center justify-between text-[11px] font-mono uppercase opacity-90">
              <span>Projected Total</span>
              {isProjectedOverBudget ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
            </div>
            <div className="text-xl font-bold font-mono">
              {formatCurrency(summary.projectedTotalExpenditure, true)}
            </div>
            <div className="text-[11px] font-mono flex items-center justify-between opacity-80">
              <span>Variance:</span>
              <span className="font-bold">
                {summary.projectedBudgetVariance <= 0 ? '-' : '+'}
                {formatCurrency(Math.abs(summary.projectedBudgetVariance))}
              </span>
            </div>
          </div>
        </div>

        {/* Utilization Bar */}
        <div className="space-y-2 bg-shop-darkest p-4 rounded border border-shop-border">
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-shop-muted uppercase font-bold flex items-center gap-1.5">
              <Percent className="w-3.5 h-3.5 text-safety-amber" />
              Capital Absorption vs. Budget Cap
            </span>
            <div className="flex items-center gap-3">
              <span className="text-shop-muted">
                Remaining Under Cap:{' '}
                <strong
                  className={`font-mono ${
                    isActualOverCap ? 'text-safety-red' : 'text-safety-green'
                  }`}
                >
                  {formatCurrency(summary.remainingBudget)}
                </strong>
              </span>
              <span className="font-mono font-bold text-shop-highlight">
                {summary.budgetUtilizationPct.toFixed(1)}% Absorbed
              </span>
            </div>
          </div>

          {/* Progress Track */}
          <div className="h-3 w-full bg-shop-panel rounded-full overflow-hidden border border-shop-border flex">
            {/* Actual Spent Portion */}
            <div
              className={`h-full transition-all duration-300 ${getGaugeColor(
                summary.budgetUtilizationPct
              )}`}
              style={{ width: `${Math.min(100, summary.budgetUtilizationPct)}%` }}
              title={`Actual Spent: ${formatCurrency(summary.totalActualSpent)} (${summary.budgetUtilizationPct.toFixed(1)}%)`}
            />
            {/* Projected Remaining Portion within cap */}
            {summary.totalAllocatedBudget > summary.totalActualSpent && (
              <div
                className="h-full bg-blueprint-cyan opacity-40 transition-all duration-300"
                style={{
                  width: `${Math.min(
                    100 - summary.budgetUtilizationPct,
                    (summary.totalPendingCommitted / summary.totalAllocatedBudget) * 100
                  )}%`,
                }}
                title={`Pending Committed: ${formatCurrency(summary.totalPendingCommitted)}`}
              />
            )}
          </div>

          <div className="flex items-center justify-between text-[11px] font-mono text-shop-muted pt-1">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-safety-green inline-block" />
                Actual Spent: {formatCurrency(summary.totalActualSpent)}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-blueprint-cyan opacity-70 inline-block" />
                Pending Committed: {formatCurrency(summary.totalPendingCommitted)}
              </span>
            </div>
            <span>
              Cap Target: {formatCurrency(summary.totalAllocatedBudget)}
            </span>
          </div>
        </div>

        {/* Phase-by-Phase Sourcing Variance Breakdown */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-mono font-bold text-shop-muted uppercase tracking-wider">
              7-Phase Sourcing &amp; Expenditure Breakdown
            </h4>
            {onSelectPhase && (
              <button
                type="button"
                onClick={() => onSelectPhase('ALL')}
                className={`min-h-[44px] px-3 flex items-center justify-center text-[11px] font-mono font-bold rounded border transition-colors cursor-pointer ${
                  selectedPhase === 'ALL'
                    ? 'bg-safety-amber text-shop-darkest border-safety-amber'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light'
                }`}
              >
                SHOW ALL PHASES
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
            {phaseKeys.map((pKey) => {
              const meta = RESTORATION_PHASES[pKey];
              const pData = summary.phaseBreakdowns[pKey];
              const isSelected = selectedPhase === pKey;
              const hasItems = pData.itemCount > 0;
              const phaseOver = pData.variance > 0;

              return (
                <div
                  key={pKey}
                  onClick={() => onSelectPhase?.(pKey)}
                  className={`p-3 rounded border transition-all text-left font-mono text-xs select-none ${
                    onSelectPhase ? 'cursor-pointer' : ''
                  } ${
                    isSelected
                      ? 'bg-shop-panel border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                      : 'bg-shop-darkest border-shop-border hover:bg-shop-dark hover:border-shop-steel'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-bold text-shop-highlight uppercase tracking-wide truncate">
                      {meta.shortCode} • {meta.title}
                    </span>
                    <span className="text-[10px] text-shop-muted px-1.5 py-0.5 rounded bg-shop-panel border border-shop-border">
                      {pData.itemCount} parts
                    </span>
                  </div>

                  <div className="space-y-1 text-[11px]">
                    <div className="flex justify-between text-shop-muted">
                      <span>Spent:</span>
                      <span className="font-bold text-safety-amber">
                        {formatCurrency(pData.actualSpent)}
                      </span>
                    </div>
                    <div className="flex justify-between text-shop-muted">
                      <span>Est Total:</span>
                      <span className="text-shop-light">
                        {formatCurrency(pData.estimatedTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-shop-border">
                      <span className="text-shop-muted">Status:</span>
                      <span
                        className={`font-bold ${
                          !hasItems
                            ? 'text-shop-muted'
                            : phaseOver
                            ? 'text-safety-red'
                            : 'text-safety-green'
                        }`}
                      >
                        {!hasItems
                          ? 'Empty'
                          : phaseOver
                          ? `+${formatCurrency(pData.variance)}`
                          : `-${formatCurrency(Math.abs(pData.variance))}`}
                      </span>
                    </div>
                  </div>

                  {/* Micro progress indicator */}
                  <div className="mt-2 flex gap-1 h-1.5 bg-shop-panel rounded-full overflow-hidden">
                    <div
                      className="bg-safety-green h-full"
                      style={{
                        width: `${pData.itemCount > 0 ? (pData.installedCount / pData.itemCount) * 100 : 0}%`,
                      }}
                      title={`${pData.installedCount} Installed`}
                    />
                    <div
                      className="bg-safety-blue h-full"
                      style={{
                        width: `${pData.itemCount > 0 ? (pData.sourcedCount / pData.itemCount) * 100 : 0}%`,
                      }}
                      title={`${pData.sourcedCount} Sourced`}
                    />
                    <div
                      className="bg-safety-amber h-full"
                      style={{
                        width: `${pData.itemCount > 0 ? (pData.neededCount / pData.itemCount) * 100 : 0}%`,
                      }}
                      title={`${pData.neededCount} Needed`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TactilePanel>
  );
};
